import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, TrendingUp, FileText, Users, Shield, Brain, 
  AlertTriangle, Clock, CheckCircle, XCircle, Activity,
  Calendar, Globe, PieChart
} from 'lucide-react';
import { brandColors } from '../components/Brand';
import api from '../api/api';
import { toast } from 'sonner';

const Analytics = () => {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  
  // Analytics data
  const [documentStats, setDocumentStats] = useState({
    total: 0,
    byType: {},
    byRisk: {},
    recentUploads: 0
  });
  const [workflowStats, setWorkflowStats] = useState({
    active: 0,
    completed: 0,
    pending: 0,
    overdue: 0
  });
  const [complianceStats, setComplianceStats] = useState({
    totalActions: 0,
    anomalies: 0,
    incidents: 0,
    highRisk: 0
  });
  const [agentStats, setAgentStats] = useState({
    totalQueries: 0,
    policyQueries: 0,
    auditQueries: 0,
    vendorQueries: 0
  });

  useEffect(() => {
    document.title = 'ReasonDMS | Analytics';
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Load documents
      const docsRes = await api.listDocuments({}, headers);
      const docs = docsRes.data || [];
      
      // Calculate document stats
      const byType = {};
      const byRisk = { Low: 0, Medium: 0, High: 0 };
      docs.forEach(doc => {
        byType[doc.doc_type] = (byType[doc.doc_type] || 0) + 1;
        byRisk[doc.risk_level || 'Low'] = (byRisk[doc.risk_level || 'Low'] || 0) + 1;
      });
      
      setDocumentStats({
        total: docs.length,
        byType,
        byRisk,
        recentUploads: docs.filter(d => {
          const created = new Date(d.created_at);
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - parseInt(timeRange));
          return created > cutoff;
        }).length
      });

      // Load workflow stats
      try {
        const tasksRes = await api.getMyTasks(headers);
        const tasks = tasksRes.data?.instances || [];
        setWorkflowStats({
          active: tasks.filter(t => t.status === 'in_progress').length,
          completed: tasks.filter(t => t.status === 'completed').length,
          pending: tasks.filter(t => t.status === 'pending_approval').length,
          overdue: tasks.filter(t => t.is_overdue).length
        });
      } catch (e) {
        console.log('Workflow stats not available');
      }

      // Load compliance report
      try {
        const compRes = await api.getComplianceReport('monthly', headers);
        if (compRes.data?.report) {
          setComplianceStats({
            totalActions: compRes.data.report.total_actions || 0,
            anomalies: compRes.data.report.anomalies_detected || 0,
            incidents: compRes.data.report.security_incidents || 0,
            highRisk: compRes.data.report.high_risk_incidents || 0
          });
        }
      } catch (e) {
        console.log('Compliance stats not available');
      }

      // Load agent interaction stats
      try {
        const headers2 = getAuthHeaders();
        // Query each agent to count interactions (simplified)
        setAgentStats({
          totalQueries: 0,
          policyQueries: 0,
          auditQueries: 0,
          vendorQueries: 0
        });
      } catch (e) {
        console.log('Agent stats not available');
      }

    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue', trend }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className={`text-3xl font-bold text-${color}-600 mt-1`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
        {trend && (
          <div className="mt-3 flex items-center text-sm">
            <TrendingUp className={`w-4 h-4 mr-1 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={trend > 0 ? 'text-green-500' : 'text-red-500'}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
            <span className="text-slate-400 ml-1">{t('analytics.vsLastPeriod')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const ProgressBar = ({ label, value, max, color = 'blue' }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    return (
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-600">{label}</span>
          <span className="font-medium">{value}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: brandColors.primary }}></div>
      </div>
    );
  }

  return (
    <div data-testid="analytics-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: brandColors.primary }}>
            ReasonDMS {t('analytics.title')}
          </h1>
          <p className="text-slate-600 mt-1">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Language Selector */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-500" />
            <Select value={i18n.language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-32" data-testid="language-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="hi">हिंदी</SelectItem>
                <SelectItem value="bn">বাংলা</SelectItem>
                <SelectItem value="ta">தமிழ்</SelectItem>
                <SelectItem value="te">తెలుగు</SelectItem>
                <SelectItem value="mr">मराठी</SelectItem>
                <SelectItem value="gu">ગુજરાતી</SelectItem>
                <SelectItem value="kn">ಕನ್ನಡ</SelectItem>
                <SelectItem value="ml">മലയാളം</SelectItem>
                <SelectItem value="pa">ਪੰਜਾਬੀ</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Time Range Selector */}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40" data-testid="time-range-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('analytics.last7Days')}</SelectItem>
              <SelectItem value="30">{t('analytics.last30Days')}</SelectItem>
              <SelectItem value="90">{t('analytics.last90Days')}</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={loadAnalytics} variant="outline">
            <Activity className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          title={t('analytics.totalDocuments')}
          value={documentStats.total}
          subtitle={`${documentStats.recentUploads} ${t('analytics.newInPeriod')}`}
          color="blue"
        />
        <StatCard
          icon={Activity}
          title={t('analytics.activeWorkflows')}
          value={workflowStats.active}
          subtitle={`${workflowStats.pending} ${t('analytics.pendingApproval')}`}
          color="purple"
        />
        <StatCard
          icon={Shield}
          title={t('analytics.securityScore')}
          value={complianceStats.totalActions > 0 ? Math.round(100 - (complianceStats.highRisk / Math.max(complianceStats.totalActions, 1)) * 100) : 100}
          subtitle={`${complianceStats.incidents} ${t('analytics.incidents')}`}
          color="green"
        />
        <StatCard
          icon={Brain}
          title={t('analytics.aiInteractions')}
          value={agentStats.totalQueries}
          subtitle={t('analytics.acrossAllAgents')}
          color="orange"
        />
      </div>

      {/* Document Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-500" />
              {t('analytics.documentsByType')}
            </CardTitle>
            <CardDescription>{t('analytics.documentDistribution')}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.entries(documentStats.byType).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(documentStats.byType).map(([type, count]) => (
                  <ProgressBar 
                    key={type} 
                    label={t(`documentTypes.${type.toLowerCase()}`) || type} 
                    value={count} 
                    max={documentStats.total}
                    color="blue"
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">{t('analytics.noData')}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              {t('analytics.riskDistribution')}
            </CardTitle>
            <CardDescription>{t('analytics.documentsByRisk')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <ProgressBar 
                label={t('riskLevels.low')} 
                value={documentStats.byRisk.Low || 0} 
                max={documentStats.total}
                color="green"
              />
              <ProgressBar 
                label={t('riskLevels.medium')} 
                value={documentStats.byRisk.Medium || 0} 
                max={documentStats.total}
                color="yellow"
              />
              <ProgressBar 
                label={t('riskLevels.high')} 
                value={documentStats.byRisk.High || 0} 
                max={documentStats.total}
                color="red"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow & Compliance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              {t('analytics.workflowStatus')}
            </CardTitle>
            <CardDescription>{t('analytics.workflowOverview')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <Activity className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-600">{workflowStats.active}</p>
                <p className="text-sm text-slate-600">{t('analytics.active')}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-600">{workflowStats.pending}</p>
                <p className="text-sm text-slate-600">{t('analytics.pending')}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">{workflowStats.completed}</p>
                <p className="text-sm text-slate-600">{t('analytics.completed')}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{workflowStats.overdue}</p>
                <p className="text-sm text-slate-600">{t('analytics.overdue')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              {t('analytics.complianceMetrics')}
            </CardTitle>
            <CardDescription>{t('analytics.securityOverview')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-blue-500" />
                  <span>{t('analytics.totalActions')}</span>
                </div>
                <span className="font-bold text-blue-600">{complianceStats.totalActions}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <span>{t('analytics.anomaliesDetected')}</span>
                </div>
                <span className="font-bold text-yellow-600">{complianceStats.anomalies}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <span>{t('analytics.securityIncidents')}</span>
                </div>
                <span className="font-bold text-orange-600">{complianceStats.incidents}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span>{t('analytics.highRiskIncidents')}</span>
                </div>
                <span className="font-bold text-red-600">{complianceStats.highRisk}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Agents Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            {t('analytics.aiAgentUsage')}
          </CardTitle>
          <CardDescription>{t('analytics.agentInteractions')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-medium">{t('analytics.policyAgent')}</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{agentStats.policyQueries}</p>
              <p className="text-sm text-slate-500">{t('analytics.queries')}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="font-medium">{t('analytics.auditAgent')}</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{agentStats.auditQueries}</p>
              <p className="text-sm text-slate-500">{t('analytics.queries')}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <span className="font-medium">{t('analytics.vendorAgent')}</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{agentStats.vendorQueries}</p>
              <p className="text-sm text-slate-500">{t('analytics.queries')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
